import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import OpenAI from 'openai';
import {Buffer} from "buffer";
import {from, Observable,} from 'rxjs';

import {ResponseClientRequest, ResponseCreateInfo, VectorFileStatus,} from '@meta-press/shared-plugins';

import {AIAPIResponse} from '../model/AIAPIResponse';
import {environment} from '../../../environments/environment';
import {BackendService} from '../backend-service/backend.service';
import {BackgroundTaskQueueService} from './background-task-queue.service';
import {AuthTokenService} from './auth-token.service';

@Injectable({
	providedIn: 'root',
})
export class OpenAIService {
	private backendUrl = environment.apiUrl;
	private OpenAiUrl: string = environment.AIEndPoint;

	private openaiClient: OpenAI | undefined = undefined;

	private currentApiKey: string | undefined = undefined;

	constructor(
		private backendService: BackendService,
		private backgroundTaskQueue: BackgroundTaskQueueService,
		private authTokenService: AuthTokenService,
		private http: HttpClient,
	) {
		// Subscribe to token changes to update OpenAI client headers
		this.authTokenService.tokens$.subscribe(
			(tokens: {
				accessToken: string;
				refreshToken: string;
				tokenExpiration: number;
			}) => {
				if (this.openaiClient && tokens.accessToken) {
					console.log('Token updated, reinitializing OpenAI client');
					// Reinitialize client with new token
					this.reinitializeClientWithToken(tokens.accessToken);
				}
			},
		);
	}

	/**
	 * Convert OpenAI Response to consistent AIAPIResponse format
	 */
	private convertToAIAPIResponse(
		openaiResponse: OpenAI.Responses.Response,
		backgroundTaskId?: string,
	): AIAPIResponse {
		console.log(
			`Converting OpenAI Response ${openaiResponse.id} to AIAPIResponse with backgroundTaskId: ${backgroundTaskId || 'undefined'}`,
		);

		let jsonString = openaiResponse.output_text as string;
		// Parse possible json from response
		const jsonIndex = jsonString.indexOf('```json');

		if (jsonIndex > -1) {
			// There might be some response text before the first ```json. Remove everything before it, including the ```json
			jsonString = jsonString.substring(jsonIndex + '```json'.length);

			// Find the index of the ending ```
			const jsonEndIndex = jsonString.indexOf('```');

			if (jsonEndIndex > -1) {
				// Remove the ending ```
				jsonString = jsonString.substring(0, jsonEndIndex);
			}
		}

		let parsedJson = {};
		let jsonParseError = false;
		try {
			if (jsonString.trim()) {
				parsedJson = JSON.parse(jsonString);
			}
		} catch (e) {
			jsonParseError = true;
			console.error('Error parsing JSON', e);
			console.log('JSON String: ', jsonString);
		}

		// Get file search results if available
		const fileSearchResults =
			(openaiResponse as any).file_search_results?.map(
				(result: unknown) => {
					const typedResult = result as {
						file_name?: string;
						file_id: string;
						score: number;
						content: string;
					};
					return {
						fileName: typedResult.file_name || 'Unknown file',
						fileId: typedResult.file_id,
						score: typedResult.score,
						content: typedResult.content,
					};
				},
			) || [];

		const aiResponse: AIAPIResponse = {
			rawResponse: openaiResponse.output_text,
			json: parsedJson,
			references: fileSearchResults,
			responseId: openaiResponse.id,
			backgroundTaskId: backgroundTaskId,
			jsonParseError: jsonParseError,
			error: undefined,
		};

		console.log(
			`Created AIAPIResponse with responseId: ${openaiResponse.id} and backgroundTaskId: ${backgroundTaskId || 'undefined'}`,
		);
		return aiResponse;
	}

	/**
	 * Creates a fetch-compatible function that uses Angular HttpClient
	 * This allows OpenAI SDK calls to go through Angular interceptors
	 */
	private createAngularFetch(): typeof fetch {
		return (
			input: RequestInfo | URL,
			init?: RequestInit,
		): Promise<Response> => {
			// Convert RequestInfo/URL to string
			const url = typeof input === 'string' ? input : input.toString();

			// Extract method, headers, and body from RequestInit
			const method = init?.method || 'GET';
			const body = init?.body;
			const headers = init?.headers;

			// List of forbidden headers that browsers don't allow JavaScript to set
			const forbiddenHeaders = [
				'accept-charset',
				'accept-encoding',
				'access-control-request-headers',
				'access-control-request-method',
				'connection',
				'content-length',
				'cookie',
				'date',
				'dnt',
				'expect',
				'host',
				'keep-alive',
				'origin',
				'referer',
				'te',
				'trailer',
				'transfer-encoding',
				'upgrade',
				'user-agent',
				'via',
			];

			// Convert Headers to object format for Angular HttpClient, filtering out forbidden headers
			const httpHeaders: { [key: string]: string } = {};
			if (headers) {
				if (headers instanceof Headers) {
					headers.forEach((value, key) => {
						// Skip forbidden headers
						if (!forbiddenHeaders.includes(key.toLowerCase())) {
							httpHeaders[key] = value;
						}
					});
				} else if (Array.isArray(headers)) {
					// Handle [string, string][] format
					headers.forEach(([key, value]) => {
						// Skip forbidden headers
						if (!forbiddenHeaders.includes(key.toLowerCase())) {
							httpHeaders[key] = value;
						}
					});
				} else {
					// Handle Record<string, string> format
					Object.entries(headers).forEach(([key, value]) => {
						// Skip forbidden headers
						if (!forbiddenHeaders.includes(key.toLowerCase())) {
							httpHeaders[key] = value as string;
						}
					});
				}
			}

			// Use Angular HttpClient to make the request
			const httpRequest = this.http.request(method, url, {
				body: body,
				headers: httpHeaders,
				observe: 'response',
				responseType: 'text', // We'll handle parsing manually to maintain fetch compatibility
			});

			// Convert Angular HttpClient Observable to fetch-compatible Promise<Response>
			return httpRequest
				.toPromise()
				.then((response) => {
					if (!response) {
						throw new Error('No response received');
					}

					// Create a fetch-compatible Response object
					const fetchResponse = new Response(response.body || '', {
						status: response.status,
						statusText: response.statusText,
						headers: new Headers(
							response.headers.keys().reduce(
								(acc, key) => {
									acc[key] = response.headers.get(key) || '';
									return acc;
								},
								{} as Record<string, string>,
							),
						),
					});

					return fetchResponse;
				})
				.catch((error) => {
					// Angular HttpClient error format differs from fetch
					// Convert it to a fetch-compatible error
					if (error.status) {
						// Create a Response object for HTTP errors (like 401, 500, etc.)
						const errorResponse = new Response(
							error.error || error.message,
							{
								status: error.status,
								statusText: error.statusText || 'Error',
							},
						);
						return errorResponse;
					}
					// For network errors or other issues, throw the error
					throw error;
				});
		};
	}

	// Reinitialize OpenAI client with updated token
	private reinitializeClientWithToken(accessToken: string): void {
		if (this.openaiClient && this.currentApiKey) {
			this.openaiClient = new OpenAI({
				apiKey: this.currentApiKey,
				dangerouslyAllowBrowser: true,
				baseURL: environment.apiUrl + environment.AIEndPoint,
				defaultHeaders: {
					Authorization: `Bearer ${accessToken}`,
				},
				fetch: this.createAngularFetch(), // Use custom fetch that goes through Angular interceptors
			});
		}
	}

	// Initialize OpenAI Client
	public InitializeClient(apiKey: string): void {
		this.currentApiKey = apiKey;
		const accessToken = this.authTokenService.getAccessToken();
		console.log(
			'Initializing OpenAI client with auth token length:',
			accessToken?.length,
		);
		console.log(
			'Using Angular HttpClient through custom fetch wrapper for OpenAI calls',
		);

		this.openaiClient = new OpenAI({
			apiKey: apiKey,
			dangerouslyAllowBrowser: true,
			baseURL: environment.apiUrl + environment.AIEndPoint,
			defaultHeaders: accessToken
				? {
						Authorization: `Bearer ${accessToken}`,
					}
				: undefined,
			fetch: this.createAngularFetch(), // Use custom fetch that goes through Angular interceptors
		});
	}

	// De-initialize OpenAI Client and Admin Client
	public DeInitializeClient(): void {
		console.log('De-initializing OpenAI client...');
		this.openaiClient = undefined;

		this.currentApiKey = undefined;
		console.log('OpenAI Client de-initialized successfully');
	}

	public generateImage(
		prompt: string,
	): Observable<OpenAI.Images.ImagesResponse> {
		if (!this.openaiClient)
			throw new Error('OpenAI client not initialized');

		return from(
			this.openaiClient!.images.generate({
				model: 'dall-e-3',
				prompt: prompt,
				n: 1,
				size: '1024x1024',
				response_format: 'url',
			}),
		);
	}

	public UploadFileFromAzureToOpenai(itemIds: number[]): Observable<any> {
		const items = {
			AzureItem: itemIds,
		};
		return this.http.post(
			`${this.backendUrl}/AzureOpenAi/FilesToOpenai`,
			items,
		);
	}

	//openai
	public CheckAndUploadToOpenai(fileIds: number[]): Observable<any> {
		return this.http.post(
			`${this.backendUrl + this.OpenAiUrl}/VectorStore/checkAndUpload`,
			fileIds,
		);
	}

	public GetStoreFilesStatus(
		fileIds: number[],
	): Observable<VectorFileStatus[]> {
		let query = 'ids=' + fileIds[0];
		query = fileIds.slice(1).reduce((acc, id) => acc + '&ids=' + id, query);
		return this.http.get<VectorFileStatus[]>(
			`${this.backendUrl + this.OpenAiUrl}/Vector_store/Status?` + query,
		);
	}

	public ClearVectorStore(): Observable<any> {
		return this.http.delete(
			`${this.backendUrl + this.OpenAiUrl}/Vector_store/files/clear`,
		);
	}

	public CreateResponse(
		request: ResponseClientRequest,
	): Observable<ResponseCreateInfo> {
		return this.http.post<ResponseCreateInfo>(
			`${this.backendUrl + this.OpenAiUrl}/responses`,
			request,
		);
	}

    public GetResponse(id: string, model: string): Observable<ResponseCreateInfo> {
        const encodedModel = Buffer.from(model, 'binary').toString('base64');
        return this.http.get<ResponseCreateInfo>(
            `${this.backendUrl + this.OpenAiUrl}/responses/${id}/${encodedModel}`
		);
	}

    public CancelResponse(id: string, model: string): Observable<any> {
		return this.http.post(
			`${this.backendUrl + this.OpenAiUrl}/responses/${id}/cancel`,
            model,
		);
	}
}
